import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

/** Panel max-widths. `md` is the default (small forms); the larger sizes give
 *  wide, data-dense content (e.g. the walk-in availability heatmap) room to
 *  breathe on a wide screen instead of stacking into a tall narrow column. */
const WIDTHS = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  wide: 'max-w-6xl',
} as const;

type ModalSize = keyof typeof WIDTHS;

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  testId?: string;
  /** Panel width. Defaults to `md`; use `wide` for data-dense content. */
  size?: ModalSize;
}

/** Thin overlay shell replicating the app's established modal chrome
 *  (see ManualDepositModal). Renders nothing when closed. */
export default function ModalShell({ open, onClose, title, children, testId, size = 'md' }: Props) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 8, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`bg-ink-deep border border-gold-dim/40 rounded-xl w-full ${WIDTHS[size]} max-h-[90vh] flex flex-col p-6 relative`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={title}
            data-testid={testId ?? 'modal-shell'}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 text-cream-soft hover:text-cream"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <h2 className="label-small-caps mb-4 shrink-0 pr-6">{title}</h2>
            <div className="overflow-y-auto min-h-0">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
